import {
  AppCategory,
  ConditionExpression,
  IdentificationProvider,
  IndividualsScreeningProvider,
  TConfigItemType,
  VERIFICATION_REQUEST_CONDITION_DOC,
  VERIFICATION_REQUEST_CONDITION_FIELDS,
  VerificationAppDocumentType,
} from "../../models";
import {
  isQuestionnaireContentAllowed,
  QUESTIONNAIRE_CONTENT_PATTERN,
} from "../QuestionnaireTab/questionnaireContentOptions";

export type LogicError = {
  path: string;
  rule: string;
  message: string;
};

type Condition = ConditionExpression;

type DynamicString = Record<string, Condition | "default">;

type DynamicDefault = { doc?: string; field: string };

type FieldConfig = {
  name?: string;
  type: string;
  label?: string | DynamicString;
  subtitle?: string | DynamicString;
  content?: string;
  default?: unknown | DynamicDefault;
  required?: boolean | Condition;
  visible?: Condition;
};

type StepRequirementConfig = {
  message?: string;
  condition?: Condition;
};

type StepConfig = {
  type: string;
  name?: string;
  fields?: FieldConfig[];
  requirements?: StepRequirementConfig[];
  apps?: {
    representative?: string;
    ubo?: string;
  };
  amlbot?: {
    format?: "default" | "doc_only";
  };
};

type OnboardingConfig = {
  category?: AppCategory;
  identification_provider?: IdentificationProvider;
  individuals_screening_provider?: IndividualsScreeningProvider;
  constants?: Record<string, unknown>;
  conditions?: Record<string, Condition>;
  elements?: StepConfig[];
};

type ConditionSlot = {
  condition: Condition;
  path: string;
  stepIndex?: number;
  fieldIndex?: number;
};

export type ConditionValidationMode = "fieldCondition" | "namedCondition" | "stepRequirement";

type ConditionValidationContext = {
  config: OnboardingConfig;
  condition: Condition;
  path: string;
  stepIndex?: number;
  fieldIndex?: number;
  validationMode?: ConditionValidationMode;
};

export const FIELD_NAME_PATTERN = /^[a-z]+[a-z0-9]*(_[a-z]+[a-z0-9]*)*$/;

const REQUIRED_FIELD_NAMES_BY_STEP_TYPE: Partial<Record<VerificationAppDocumentType, string[]>> = {
  [VerificationAppDocumentType.UserInformation]: ["first_name", "last_name", "date_of_birth"],
  [VerificationAppDocumentType.EntityInformation]: ["entity_name"],
  [VerificationAppDocumentType.BeneficialOwnership]: ["is_pep"],
};

const VERIFICATION_REQUEST_CONDITION_FIELD_SET = new Set<string>(
  VERIFICATION_REQUEST_CONDITION_FIELDS,
);

function hasMeaningfulStepName(name: string | undefined): name is string {
  return typeof name === "string" && name.trim().length > 0;
}

function getEffectiveStepName(step: StepConfig): string {
  return hasMeaningfulStepName(step.name) ? step.name : step.type;
}

function isConditionObject(
  obj: unknown,
): obj is { field: string; op: string; const?: string; doc?: string } {
  return (
    typeof obj === "object"
    && obj !== null
    && "field" in obj
    && "op" in obj
    && typeof (obj as Record<string, unknown>).field === "string"
  );
}

function isOrCondition(obj: unknown): obj is { OR: Condition[] } {
  return typeof obj === "object" && obj !== null && "OR" in obj;
}

function isNotCondition(obj: unknown): obj is { NOT: Condition } {
  return typeof obj === "object" && obj !== null && "NOT" in obj;
}

function isDynamicDefault(obj: unknown): obj is DynamicDefault {
  return (
    typeof obj === "object"
    && obj !== null
    && "field" in obj
    && typeof (obj as Record<string, unknown>).field === "string"
    && !("op" in obj)
  );
}

function isVerificationRequestConditionRef(
  condition: { doc?: string },
): boolean {
  return condition.doc === VERIFICATION_REQUEST_CONDITION_DOC;
}

function isDynamicString(obj: unknown): obj is DynamicString {
  return typeof obj === "object" && obj !== null && !Array.isArray(obj);
}

function isNoteFieldConfig(field: FieldConfig): boolean {
  return field.type === TConfigItemType.NOTE;
}

function isNamedValueFieldConfig(
  field: FieldConfig,
): field is FieldConfig & { name: string } {
  return !isNoteFieldConfig(field) && typeof field.name === "string";
}

function getValueFieldNames(fields: FieldConfig[] | undefined): Set<string> {
  return new Set(
    fields
      ?.filter(isNamedValueFieldConfig)
      .map((field) => field.name) || [],
  );
}

function collectConstRefs(condition: Condition, refs: Set<string>): void {
  if (Array.isArray(condition)) {
    condition.forEach((item) => collectConstRefs(item, refs));
  } else if (isOrCondition(condition)) {
    condition.OR.forEach((item) => collectConstRefs(item, refs));
  } else if (isNotCondition(condition)) {
    collectConstRefs(condition.NOT, refs);
  } else if (isConditionObject(condition) && condition.const) {
    refs.add(condition.const);
  }
}

function collectDocRefs(condition: Condition, refs: Set<string>): void {
  if (Array.isArray(condition)) {
    condition.forEach((item) => collectDocRefs(item, refs));
  } else if (isOrCondition(condition)) {
    condition.OR.forEach((item) => collectDocRefs(item, refs));
  } else if (isNotCondition(condition)) {
    collectDocRefs(condition.NOT, refs);
  } else if (
    isConditionObject(condition)
    && condition.doc
    && !isVerificationRequestConditionRef(condition)
  ) {
    refs.add(condition.doc);
  }
}

function collectVerificationRequestFieldRefs(condition: Condition, refs: Set<string>): void {
  if (Array.isArray(condition)) {
    condition.forEach((item) => collectVerificationRequestFieldRefs(item, refs));
  } else if (isOrCondition(condition)) {
    condition.OR.forEach((item) => collectVerificationRequestFieldRefs(item, refs));
  } else if (isNotCondition(condition)) {
    collectVerificationRequestFieldRefs(condition.NOT, refs);
  } else if (isConditionObject(condition) && isVerificationRequestConditionRef(condition)) {
    refs.add(condition.field);
  }
}

function collectFieldRefs(condition: Condition, refs: Set<string>): void {
  if (Array.isArray(condition)) {
    condition.forEach((item) => collectFieldRefs(item, refs));
  } else if (isOrCondition(condition)) {
    condition.OR.forEach((item) => collectFieldRefs(item, refs));
  } else if (isNotCondition(condition)) {
    collectFieldRefs(condition.NOT, refs);
  } else if (isConditionObject(condition) && !condition.doc && condition.op !== "condition") {
    refs.add(condition.field);
  }
}

function collectConditionRefs(condition: Condition, refs: Set<string>): void {
  if (Array.isArray(condition)) {
    condition.forEach((item) => collectConditionRefs(item, refs));
  } else if (isOrCondition(condition)) {
    condition.OR.forEach((item) => collectConditionRefs(item, refs));
  } else if (isNotCondition(condition)) {
    collectConditionRefs(condition.NOT, refs);
  } else if (isConditionObject(condition) && condition.op === "condition") {
    refs.add(condition.field);
  }
}

function pushConditionSlot(slots: ConditionSlot[], slot: ConditionSlot): void {
  slots.push(slot);
}

function collectDynamicStringSlots(
  slots: ConditionSlot[],
  value: string | DynamicString | undefined,
  path: string,
  stepIndex: number,
  fieldIndex: number,
): void {
  if (!value || !isDynamicString(value)) {
    return;
  }

  Object.values(value).forEach((condition) => {
    if (condition === "default") {
      return;
    }

    pushConditionSlot(slots, {
      condition,
      path,
      stepIndex,
      fieldIndex,
    });
  });
}

function getFieldConditionSlots(config: OnboardingConfig): ConditionSlot[] {
  const slots: ConditionSlot[] = [];

  config.elements?.forEach((element, elementIdx) => {
    if (element.type === "Identification") {
      return;
    }

    element.fields?.forEach((field, fieldIdx) => {
      if (field.visible && typeof field.visible !== "boolean") {
        pushConditionSlot(slots, {
          condition: field.visible as Condition,
          path: `/elements/${elementIdx}/fields/${fieldIdx}/visible`,
          stepIndex: elementIdx,
          fieldIndex: fieldIdx,
        });
      }

      if (field.required && typeof field.required !== "boolean") {
        pushConditionSlot(slots, {
          condition: field.required as Condition,
          path: `/elements/${elementIdx}/fields/${fieldIdx}/required`,
          stepIndex: elementIdx,
          fieldIndex: fieldIdx,
        });
      }

      collectDynamicStringSlots(
        slots,
        field.label,
        `/elements/${elementIdx}/fields/${fieldIdx}/label`,
        elementIdx,
        fieldIdx,
      );
      collectDynamicStringSlots(
        slots,
        field.subtitle,
        `/elements/${elementIdx}/fields/${fieldIdx}/subtitle`,
        elementIdx,
        fieldIdx,
      );
    });
  });

  return slots;
}

function getRequirementConditionSlots(config: OnboardingConfig): ConditionSlot[] {
  const slots: ConditionSlot[] = [];

  config.elements?.forEach((element, elementIdx) => {
    element.requirements?.forEach((requirement, requirementIdx) => {
      if (!requirement.condition) {
        return;
      }

      pushConditionSlot(slots, {
        condition: requirement.condition,
        path: `/elements/${elementIdx}/requirements/${requirementIdx}/condition`,
        stepIndex: elementIdx,
      });
    });
  });

  return slots;
}

function getNamedConditionSlots(config: OnboardingConfig): ConditionSlot[] {
  if (!config.conditions) {
    return [];
  }

  return Object.entries(config.conditions).map(([name, condition]) => ({
    condition,
    path: `/conditions/${name}`,
  }));
}

function appendConstantReferenceErrors(
  context: ConditionValidationContext,
  errors: LogicError[],
): void {
  const definedConstants = new Set(Object.keys(context.config.constants || {}));
  const refs = new Set<string>();
  collectConstRefs(context.condition, refs);

  refs.forEach((constRef) => {
    if (!definedConstants.has(constRef)) {
      errors.push({
        path: context.path,
        rule: "constant_exists",
        message: `Condition references undefined constant '${constRef}'`,
      });
    }
  });
}

function appendConditionReferenceErrors(
  context: ConditionValidationContext,
  errors: LogicError[],
): void {
  const definedConditions = new Set(Object.keys(context.config.conditions || {}));
  const refs = new Set<string>();
  collectConditionRefs(context.condition, refs);

  refs.forEach((conditionRef) => {
    if (!definedConditions.has(conditionRef)) {
      errors.push({
        path: context.path,
        rule: "condition_exists",
        message: `Condition references undefined named condition '${conditionRef}'`,
      });
    }
  });
}

function appendDisallowedNamedConditionReferenceErrors(
  context: ConditionValidationContext,
  errors: LogicError[],
): void {
  const refs = new Set<string>();
  collectConditionRefs(context.condition, refs);

  refs.forEach((conditionRef) => {
    errors.push({
      path: context.path,
      rule: "named_condition_reference_disallowed",
      message: `Named conditions cannot reference other named conditions ('${conditionRef}')`,
    });
  });
}

function appendDocumentReferenceErrors(
  context: ConditionValidationContext,
  errors: LogicError[],
): void {
  const definedStepNames = new Set(
    context.config.elements?.map((element) => getEffectiveStepName(element)) || [],
  );
  const refs = new Set<string>();
  collectDocRefs(context.condition, refs);

  refs.forEach((docRef) => {
    if (!definedStepNames.has(docRef)) {
      errors.push({
        path: context.path,
        rule: "step_exists",
        message: `Condition references undefined step '${docRef}'`,
      });
    }
  });
}

function appendVerificationRequestFieldErrors(
  context: ConditionValidationContext,
  errors: LogicError[],
): void {
  const refs = new Set<string>();
  collectVerificationRequestFieldRefs(context.condition, refs);

  refs.forEach((fieldRef) => {
    if (VERIFICATION_REQUEST_CONDITION_FIELD_SET.has(fieldRef)) {
      return;
    }

    errors.push({
      path: context.path,
      rule: "verification_request_field",
      message: `Verification request conditions can only reference fields: ${VERIFICATION_REQUEST_CONDITION_FIELDS.join(", ")}`,
    });
  });
}

function appendFieldReferenceErrors(
  context: ConditionValidationContext,
  errors: LogicError[],
): void {
  if (context.stepIndex === undefined) {
    return;
  }

  const currentStep = context.config.elements?.[context.stepIndex];
  const fieldNames = getValueFieldNames(currentStep?.fields);
  const refs = new Set<string>();
  collectFieldRefs(context.condition, refs);

  refs.forEach((fieldRef) => {
    if (!fieldNames.has(fieldRef)) {
      errors.push({
        path: context.path,
        rule: "field_exists",
        message: `Condition references undefined field '${fieldRef}' in this step`,
      });
    }
  });
}

function appendNamedConditionFieldErrors(
  context: ConditionValidationContext,
  errors: LogicError[],
): void {
  if (context.stepIndex === undefined || !context.config.conditions) {
    return;
  }

  const currentStep = context.config.elements?.[context.stepIndex];
  const fieldNames = getValueFieldNames(currentStep?.fields);
  const namedConditionRefs = new Set<string>();
  collectConditionRefs(context.condition, namedConditionRefs);

  namedConditionRefs.forEach((conditionName) => {
    const namedCondition = context.config.conditions?.[conditionName];

    if (!namedCondition) {
      return;
    }

    const refs = new Set<string>();
    collectFieldRefs(namedCondition, refs);

    refs.forEach((fieldRef) => {
      if (!fieldNames.has(fieldRef)) {
        errors.push({
          path: context.path,
          rule: "named_condition_field_exists",
          message: `Named condition '${conditionName}' references field '${fieldRef}' which doesn't exist in this step`,
        });
      }
    });
  });
}

function appendReferenceOrderErrors(
  context: ConditionValidationContext,
  errors: LogicError[],
): void {
  if (context.stepIndex === undefined) {
    return;
  }

  const stepIndexMap = new Map<string, number>();
  context.config.elements?.forEach((element, index) => {
    stepIndexMap.set(getEffectiveStepName(element), index);
  });

  const docRefs = new Set<string>();
  collectDocRefs(context.condition, docRefs);

  docRefs.forEach((docRef) => {
    const referencedStepIndex = stepIndexMap.get(docRef);

    if (referencedStepIndex !== undefined && referencedStepIndex >= context.stepIndex!) {
      errors.push({
        path: context.path,
        rule: "reference_order_step",
        message: `Condition references step '${docRef}' which appears at or after the current step (must reference previous steps only)`,
      });
    }
  });

  if (context.fieldIndex === undefined) {
    return;
  }

  const currentStep = context.config.elements?.[context.stepIndex];
  const fieldIndexMap = new Map<string, number>();
  currentStep?.fields?.forEach((field, index) => {
    if (isNamedValueFieldConfig(field)) {
      fieldIndexMap.set(field.name, index);
    }
  });

  const fieldRefs = new Set<string>();
  collectFieldRefs(context.condition, fieldRefs);

  fieldRefs.forEach((fieldRef) => {
    const referencedFieldIndex = fieldIndexMap.get(fieldRef);

    if (referencedFieldIndex !== undefined && referencedFieldIndex > context.fieldIndex!) {
      errors.push({
        path: context.path,
        rule: "reference_order_field",
        message: `Condition references field '${fieldRef}' which appears after the current field (must reference current or previous fields only)`,
      });
    }
  });
}

function validateFieldNameFormat(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  config.elements?.forEach((element, elementIdx) => {
    if (element.type === "Identification") {
      return;
    }

    element.fields?.forEach((field, fieldIdx) => {
      if (isNoteFieldConfig(field)) {
        return;
      }

      if (!field.name || !FIELD_NAME_PATTERN.test(field.name)) {
        errors.push({
          path: `/elements/${elementIdx}/fields/${fieldIdx}/name`,
          rule: "field_name_format",
          message: `Field name '${field.name}' must be snake_case (lowercase with single underscores)`,
        });
      }
    });
  });
}

function validateFieldContent(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  config.elements?.forEach((element, elementIdx) => {
    if (element.type === "Identification") {
      return;
    }

    element.fields?.forEach((field, fieldIdx) => {
      if (field.content === undefined) {
        return;
      }

      const path = `/elements/${elementIdx}/fields/${fieldIdx}/content`;

      if (!QUESTIONNAIRE_CONTENT_PATTERN.test(field.content)) {
        errors.push({
          path,
          rule: "field_content_format",
          message: `Field content '${field.content}' must be a dot-separated semantic identifier`,
        });
        return;
      }

      if (!isQuestionnaireContentAllowed(
        config.category,
        field.type as TConfigItemType,
        field.content,
      )) {
        errors.push({
          path,
          rule: "field_content_allowed",
          message: `Field content '${field.content}' is not allowed for category '${config.category ?? "any"}' and field type '${field.type}'`,
        });
      }
    });
  });
}

function validateCustomStepNames(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  config.elements?.forEach((element, idx) => {
    if (element.type !== "Custom" || hasMeaningfulStepName(element.name)) {
      return;
    }

    errors.push({
      path: `/elements/${idx}/name`,
      rule: "custom_step_name_required",
      message: "Custom steps must define a non-empty name",
    });
  });
}

function validateStepNameUniqueness(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  if (!config.elements) {
    return;
  }

  const seen = new Map<string, number>();

  config.elements.forEach((element, idx) => {
    const name = getEffectiveStepName(element);
    const previousIndex = seen.get(name);

    if (previousIndex !== undefined) {
      errors.push({
        path: `/elements/${idx}`,
        rule: "step_name_unique",
        message: `Step name '${name}' is not unique (also used at /elements/${previousIndex})`,
      });
    } else {
      seen.set(name, idx);
    }
  });
}

function validateRequiredFieldPresence(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  config.elements?.forEach((element, elementIdx) => {
    const requiredFieldNames = REQUIRED_FIELD_NAMES_BY_STEP_TYPE[element.type as VerificationAppDocumentType];

    if (!requiredFieldNames) {
      return;
    }

    const configuredFieldNames = getValueFieldNames(element.fields);

    requiredFieldNames.forEach((fieldName) => {
      if (configuredFieldNames.has(fieldName)) {
        return;
      }

      errors.push({
        path: `/elements/${elementIdx}/fields`,
        rule: "required_field_present",
        message: `${element.type} steps must include a field named '${fieldName}'`,
      });
    });
  });
}

function validateIndividualsScreeningProvider(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  if (
    config.category === AppCategory.ENTITY
    || config.individuals_screening_provider === undefined
  ) {
    return;
  }

  const identificationProvider =
    config.identification_provider ?? IdentificationProvider.AMLBOT;
  const screeningProvider = config.individuals_screening_provider;
  const isCompatible =
    identificationProvider === IdentificationProvider.MANUAL
      ? screeningProvider === IndividualsScreeningProvider.MANUAL
      : screeningProvider !== IndividualsScreeningProvider.MANUAL;

  if (!isCompatible) {
    errors.push({
      path: "/individuals_screening_provider",
      rule: "individuals_screening_provider_compatible",
      message: `Individuals screening provider '${screeningProvider}' is not compatible with identification provider '${identificationProvider}'`,
    });
  }
}

function validateConstantReferences(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  [
    ...getNamedConditionSlots(config),
    ...getFieldConditionSlots(config),
    ...getRequirementConditionSlots(config),
  ].forEach((slot) => {
    appendConstantReferenceErrors(
      {
        config,
        ...slot,
      },
      errors,
    );
  });
}

function validateDocumentReferences(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  [
    ...getNamedConditionSlots(config),
    ...getFieldConditionSlots(config),
    ...getRequirementConditionSlots(config),
  ].forEach((slot) => {
    appendDocumentReferenceErrors(
      {
        config,
        ...slot,
      },
      errors,
    );
  });

  const definedStepNames = new Set(
    config.elements?.map((element) => getEffectiveStepName(element)) || [],
  );

  config.elements?.forEach((element, elementIdx) => {
    if (element.type === "Identification") {
      return;
    }

    element.fields?.forEach((field, fieldIdx) => {
      if (isDynamicDefault(field.default) && field.default.doc && !definedStepNames.has(field.default.doc)) {
        errors.push({
          path: `/elements/${elementIdx}/fields/${fieldIdx}/default`,
          rule: "step_exists",
          message: `Default references undefined step '${field.default.doc}'`,
        });
      }
    });
  });
}

function validateVerificationRequestFieldReferences(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  [
    ...getNamedConditionSlots(config),
    ...getFieldConditionSlots(config),
    ...getRequirementConditionSlots(config),
  ].forEach((slot) => {
    appendVerificationRequestFieldErrors(
      {
        config,
        ...slot,
      },
      errors,
    );
  });
}

function validateFieldReferences(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  [
    ...getFieldConditionSlots(config),
    ...getRequirementConditionSlots(config),
  ].forEach((slot) => {
    appendFieldReferenceErrors(
      {
        config,
        ...slot,
      },
      errors,
    );
  });

  config.elements?.forEach((element, elementIdx) => {
    if (element.type === "Identification") {
      return;
    }

    const fieldNames = getValueFieldNames(element.fields);

    element.fields?.forEach((field, fieldIdx) => {
      if (isDynamicDefault(field.default) && !field.default.doc && !fieldNames.has(field.default.field)) {
        errors.push({
          path: `/elements/${elementIdx}/fields/${fieldIdx}/default`,
          rule: "field_exists",
          message: `Default references undefined field '${field.default.field}' in this step`,
        });
      }
    });
  });
}

function validateConditionReferences(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  [
    ...getFieldConditionSlots(config),
    ...getRequirementConditionSlots(config),
  ].forEach((slot) => {
    appendConditionReferenceErrors(
      {
        config,
        ...slot,
      },
      errors,
    );
  });
}

function validateNamedConditionFieldsAtUsage(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  [
    ...getFieldConditionSlots(config),
    ...getRequirementConditionSlots(config),
  ].forEach((slot) => {
    appendNamedConditionFieldErrors(
      {
        config,
        ...slot,
      },
      errors,
    );
  });
}

function validateReferenceOrder(
  config: OnboardingConfig,
  errors: LogicError[],
): void {
  [
    ...getFieldConditionSlots(config),
    ...getRequirementConditionSlots(config),
  ].forEach((slot) => {
    appendReferenceOrderErrors(
      {
        config,
        ...slot,
      },
      errors,
    );
  });

  if (!config.elements) {
    return;
  }

  const stepIndexMap = new Map<string, number>();
  config.elements.forEach((element, idx) => {
    stepIndexMap.set(getEffectiveStepName(element), idx);
  });

  config.elements.forEach((element, elementIdx) => {
    if (element.type === "Identification") {
      return;
    }

    const fieldIndexMap = new Map<string, number>();
    element.fields?.forEach((field, index) => {
      if (isNamedValueFieldConfig(field)) {
        fieldIndexMap.set(field.name, index);
      }
    });

    element.fields?.forEach((field, fieldIdx) => {
      if (!isDynamicDefault(field.default)) {
        return;
      }

      if (field.default.doc) {
        const referencedStepIndex = stepIndexMap.get(field.default.doc);

        if (referencedStepIndex !== undefined && referencedStepIndex >= elementIdx) {
          errors.push({
            path: `/elements/${elementIdx}/fields/${fieldIdx}/default`,
            rule: "reference_order_step",
            message: `Default references step '${field.default.doc}' which appears at or after the current step (must reference previous steps only)`,
          });
        }

        return;
      }

      const referencedFieldIndex = fieldIndexMap.get(field.default.field);

      if (referencedFieldIndex !== undefined && referencedFieldIndex > fieldIdx) {
        errors.push({
          path: `/elements/${elementIdx}/fields/${fieldIdx}/default`,
          rule: "reference_order_field",
          message: `Default references field '${field.default.field}' which appears after the current field (must reference current or previous fields only)`,
        });
      }
    });
  });
}

export function validateConditionInContext(
  context: ConditionValidationContext,
): LogicError[] {
  const errors: LogicError[] = [];
  const validationMode = context.validationMode ?? "fieldCondition";

  appendConstantReferenceErrors(context, errors);
  appendDocumentReferenceErrors(context, errors);
  appendVerificationRequestFieldErrors(context, errors);

  if (validationMode === "namedCondition") {
    appendDisallowedNamedConditionReferenceErrors(context, errors);
    return errors;
  }

  appendConditionReferenceErrors(context, errors);
  appendFieldReferenceErrors(context, errors);
  appendNamedConditionFieldErrors(context, errors);
  appendReferenceOrderErrors(context, errors);

  return errors;
}

export function validateLogic(config: unknown): LogicError[] {
  const errors: LogicError[] = [];

  if (typeof config !== "object" || config === null) {
    return errors;
  }

  const cfg = config as OnboardingConfig;

  validateFieldNameFormat(cfg, errors);
  validateFieldContent(cfg, errors);
  validateCustomStepNames(cfg, errors);
  validateStepNameUniqueness(cfg, errors);
  validateRequiredFieldPresence(cfg, errors);
  validateIndividualsScreeningProvider(cfg, errors);
  validateConstantReferences(cfg, errors);
  validateConditionReferences(cfg, errors);
  validateVerificationRequestFieldReferences(cfg, errors);
  validateDocumentReferences(cfg, errors);
  validateFieldReferences(cfg, errors);
  validateNamedConditionFieldsAtUsage(cfg, errors);
  validateReferenceOrder(cfg, errors);

  return errors;
}
